import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Check, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/roles";
import { shortDate } from "@/lib/format";
import { useDepartmentNames } from "@/hooks/useDepartmentNames";
import {
  useDeclineSupportQueueItem, usePromoteSupportQueueItem, useSetApprovalOverride, useSupportQueue,
  useSyncSupportQueue, useUpdateSupportQueueItem, type SupportQueueItem,
} from "@/hooks/useSupportQueue";

/**
 * Small Enhancements on the board (Brett, 2026-10-01): one ranked list
 * of every open enhancement, prioritised by the PMO so the team pulls
 * the most impactful ones when they have capacity. No CFO gate — Approve
 * here is the approval. Lives beside Projects on Top Projects; never
 * counted in the board's cards or benefit totals.
 */
const FS = "helpdesk.etereman.com";
const IMPACT: Array<{ key: "high" | "medium" | "low"; label: string; cls: string }> = [
  { key: "high", label: "High", cls: "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-200 dark:ring-rose-500/30" },
  { key: "medium", label: "Medium", cls: "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30" },
  { key: "low", label: "Low", cls: "bg-raised text-ink-3 ring-line" },
];
const FS_PRIORITY: Record<number, string> = { 1: "Low", 2: "Medium", 3: "High", 4: "Urgent" };
type Filter = "all" | "awaiting" | "approved";

// Only the PMO door (2026-10-01): Request Type = Small Enhancement in the Project Management group.
const isOpen = (i: SupportQueueItem) => i.source_group === "project_management" && !(i.is_closed || i.done_manual || i.jira_status_category === "done");
const approved = (i: SupportQueueItem) => i.approval_state_effective === "approved";

export function SmallEnhancements() {
  const { isPmo } = useCan();
  const { data: items = [], isLoading } = useSupportQueue();
  const departments = useDepartmentNames();
  const update = useUpdateSupportQueueItem();
  const setApproval = useSetApprovalOverride();
  const decline = useDeclineSupportQueueItem();
  const promote = usePromoteSupportQueueItem();
  const sync = useSyncSupportQueue();
  const [filter, setFilter] = useState<Filter>("all");

  // Order (Brett, 2026-10-01): approved first, oldest received first. A
  // manual rank (arrows) only pulls an item ahead within its own group.
  const received = (i: SupportQueueItem) => (i.fs_created_at ? new Date(i.fs_created_at).getTime() : Number.MAX_SAFE_INTEGER);
  const open = useMemo(() => items.filter(isOpen).slice().sort((a, b) =>
    Number(approved(b)) - Number(approved(a)) || (a.rank ?? 1e9) - (b.rank ?? 1e9) || received(a) - received(b) || a.id - b.id), [items]);
  const shown = useMemo(() => open.filter((i) => filter === "all" ? true : filter === "approved" ? approved(i) : !approved(i)), [open, filter]);
  const counts = { all: open.length, awaiting: open.filter((i) => !approved(i)).length, approved: open.filter(approved).length };

  const move = (idx: number, dir: -1 | 1) => {
    const other = idx + dir;
    if (other < 0 || other >= shown.length) return;
    // Ranks are global across the list; seed 1..n when unranked.
    const ranks = shown.map((it, i) => it.rank ?? i + 1);
    update.mutate({ id: shown[idx].id, rank: ranks[other] });
    update.mutate({ id: shown[other].id, rank: ranks[idx] });
  };

  return (
    <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm" data-testid="small-enhancements">
      <div className="flex flex-wrap items-center gap-3 border-b border-line-soft px-5 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-ink">Small Enhancements <span className="ml-1 rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-ink-muted">{open.length}</span></h2>
          <p className="text-[11px] text-ink-muted">Approved first, oldest first — the team pulls from the top when capacity frees up. Approve here is the approval; no CFO gate.</p>
        </div>
        <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-xs">
          {([["all", "All"], ["awaiting", "Awaiting approval"], ["approved", "Approved"]] as Array<[Filter, string]>).map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} className={cn("rounded-md px-2.5 py-1 font-medium transition-colors", filter === k ? "bg-brand-navy-600 text-white shadow-sm" : "text-ink-3 hover:bg-raised")} data-testid={`enh-filter-${k}`}>
              {label} <span className={cn("ml-1 tabular-nums", filter === k ? "text-white/80" : "text-ink-subtle")}>{counts[k]}</span>
            </button>
          ))}
        </div>
        {isPmo && (
          <button onClick={() => sync.mutate(undefined, { onSuccess: (d: any) => toast.success(`Synced — ${d.fetched} in Freshservice`), onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Freshservice sync failed.") })} disabled={sync.isPending} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-ink-3 hover:bg-raised disabled:opacity-50">
            <RefreshCw className={cn("h-3.5 w-3.5", sync.isPending && "animate-spin")} /> Refresh
          </button>
        )}
      </div>
      {isLoading && items.length === 0 ? (
        <div className="m-5 h-16 animate-pulse rounded-lg bg-raised" />
      ) : shown.length === 0 ? (
        <div className="px-5 py-8 text-center text-xs text-ink-subtle">{open.length === 0 ? "No open enhancements." : "Nothing in this filter."}</div>
      ) : (
        <ul className="divide-y divide-line-soft">
          {shown.map((item, idx) => (
            <li key={item.id} className="flex items-center gap-3 px-4 py-2" data-testid="enh-row" data-ticket={item.fs_ticket_id ?? ""}>
              <span className="w-6 shrink-0 text-right text-xs font-bold tabular-nums text-ink-subtle">{idx + 1}</span>
              {isPmo && (
                <span className="flex shrink-0 flex-col">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded p-0.5 text-ink-faint hover:text-ink-3 disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === shown.length - 1} className="rounded p-0.5 text-ink-faint hover:text-ink-3 disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  {item.fs_ticket_id && (
                    <a href={`https://${FS}/a/tickets/${item.fs_ticket_id}`} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1 font-mono text-[11px] text-sky-700 dark:text-sky-300 hover:underline">#{item.fs_ticket_id}<ExternalLink className="h-2.5 w-2.5" /></a>
                  )}
                  {item.source_project_id && (
                    <Link to={`/portfolio/${item.source_project_id}`} className="shrink-0 font-mono text-[11px] text-ink-subtle hover:underline" title="Sent from the board — keeps its planner number" data-testid="enh-source-project">{item.source_project_id}</Link>
                  )}
                  <span className="truncate text-sm text-ink">{item.subject}</span>
                </span>
                <span className="mt-0.5 block text-[10px] text-ink-subtle">
                  {item.requester ? `by ${item.requester}` : "submitter unknown"}{item.requested_for ? ` · for ${item.requested_for}` : ""}{item.fs_department ? ` · from ${item.fs_department}` : ""}
                  {item.fs_created_at ? ` · received ${shortDate(item.fs_created_at)}` : ""}{item.fs_priority ? ` · ticket priority ${FS_PRIORITY[item.fs_priority] ?? item.fs_priority}` : ""}
                  {item.jira_key ? ` · ${item.jira_key}${item.jira_status ? ` (${item.jira_status})` : ""}` : ""}
                </span>
              </span>
              {/* Impact — the PMO's call. */}
              <span className="flex shrink-0 items-center gap-1" data-testid="enh-impact">
                {IMPACT.map((o) => (
                  <button key={o.key} disabled={!isPmo} onClick={() => update.mutate({ id: item.id, impact: item.impact === o.key ? null : o.key })}
                    className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 transition-colors disabled:cursor-default", item.impact === o.key ? o.cls : "text-ink-faint ring-transparent hover:ring-line", !isPmo && item.impact !== o.key && "hidden")}
                    title={`Impact: ${o.label}`}>{o.label}</button>
                ))}
                {!item.impact && !isPmo && <span className="text-[10px] text-ink-faint">no impact set</span>}
              </span>
              {/* Department route */}
              {isPmo ? (
                <select value={item.department ?? ""} onChange={(e) => update.mutate({ id: item.id, department: (e.target.value || null) as any })}
                  className={cn("shrink-0 rounded-md border bg-surface px-2 py-1 text-[11px] text-ink", item.department ? "border-line" : "border-amber-300 dark:border-amber-500/40")} title={item.department ? "Reassign" : "Route to a department"}>
                  <option value="">{item.department ? "Department…" : "Route to…"}</option>
                  {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              ) : (
                <span className="shrink-0 text-[11px] text-ink-3">{item.department ?? "—"}</span>
              )}
              {/* Approval + actions */}
              {approved(item) ? (
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300" title={item.approver_name ? `Approved by ${item.approver_name}` : "Approved"}><Check className="h-3 w-3" /> Approved</span>
              ) : isPmo ? (
                <button onClick={() => setApproval.mutate({ id: item.id, approved: true }, { onSuccess: () => toast.success("Approved"), onError: () => toast.error("Couldn't approve") })}
                  className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700" data-testid="enh-approve"><Check className="h-3 w-3" /> Approve</button>
              ) : (
                <span className="shrink-0 text-[10px] text-ink-subtle">awaiting approval</span>
              )}
              {isPmo && (
                <>
                  <DeclineButton onDecline={() => decline.mutate(item.id, { onSuccess: () => toast.success("Declined"), onError: () => toast.error("Couldn't decline") })} />
                  {item.promoted_project_id ? (
                    <Link to={`/portfolio/${item.promoted_project_id}`} className="shrink-0 text-[10px] font-semibold text-brand-navy-600 dark:text-brand-navy-300 hover:underline">→ {item.promoted_project_id}</Link>
                  ) : (
                    <button onClick={() => promote.mutate(item.id, { onSuccess: (d) => toast.success(`Promoted to ${d.project_id} — now in New / unscoped`), onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Couldn't promote") })}
                      className="shrink-0 rounded-md border border-line px-2 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-brand-navy-300 hover:text-brand-navy-700 dark:hover:text-brand-navy-300" title="This is really a project">Promote</button>
                  )}
                  {approved(item) && (
                    <button onClick={() => update.mutate({ id: item.id, done_manual: true })} className="shrink-0 inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-emerald-300 hover:text-emerald-700 dark:hover:text-emerald-300" title="Mark done"><Check className="h-3 w-3" /> Done</button>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-line-soft px-5 py-2 text-[11px] text-ink-muted">
        Only Freshservice tickets with Request Type <span className="font-medium text-ink-3">Small Enhancement</span> in the Project Management group land here. Routed items are also ranked on their <Link to="/departments" className="text-brand-navy-600 dark:text-brand-navy-300 hover:underline">department pages</Link>.
      </div>
    </section>
  );
}

function DeclineButton({ onDecline }: { onDecline: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (!confirming) return <button onClick={() => setConfirming(true)} className="shrink-0 text-[11px] text-ink-subtle hover:text-rose-600 hover:underline" data-testid="enh-decline">Decline</button>;
  return (
    <span className="flex shrink-0 items-center gap-1">
      <button onClick={onDecline} className="rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700">Decline?</button>
      <button onClick={() => setConfirming(false)} className="text-[11px] text-ink-subtle hover:underline">Cancel</button>
    </span>
  );
}
